import { FC, useEffect, useState } from 'react';
import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons/faChevronLeft';
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons/faChevronRight';
import { faEllipsis } from '@fortawesome/pro-regular-svg-icons/faEllipsis';
import type { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { cn } from '../../utils';
import { FaIcon } from '../FaIcon';
import { usePagination } from './usePagination';
import { PaginationButton, PaginationLink } from './PaginationControls';
import {
  PageSizeSelect,
  PaginationPageSize,
  PaginationPageSizeItem,
} from './PageSizeSelect';

type TDir = 'rtl' | 'ltr';
export interface PaginationProps {
  className?: string;
  type: 'button' | 'link';
  dir?: TDir;
  page: number;
  totalNoItems: number;
  onPageChange?: (pageIndex: number) => void;
  onNextPage?: (pageIndex: number) => void;
  onPreviousPage?: (pageIndex: number) => void;
  setPageParams?: (page: string) => void;
  pageSizeOptions: {
    label?: string;
    container: HTMLElement;
    pageSize: PaginationPageSize;
    onPageSizeChange: (pageSize: PaginationPageSize) => void;
    options?: PaginationPageSizeItem[];
  };
  texts: {
    ariaLabel: string;
    ariaTitle: string;
    pageAriaLabel: string;
    selectedPageAriaLabel: string;
    next: string;
    prev: string;
  };
}

interface PagesProps {
  type: 'button' | 'link';
  page: number;
  onPageChange?: (page: number) => void;
  className?: string;
  totalNoPages: number;
  perPage: number;
  texts: {
    pageAriaLabel: string;
    selectedPageAriaLabel: string;
  };
}

const Ellipsis = () => (
  <span className="l-icon-btn l-btn-sm l-btn-tertiary pointer-events-none w-[22px]">
    <FaIcon icon={faEllipsis} width={12} height={12} />
  </span>
);

const Pages: FC<PagesProps> = ({
  type,
  page,
  perPage,
  totalNoPages,
  onPageChange,
  texts,
}) => {
  const PaginationControl =
    type === 'button' ? PaginationButton : PaginationLink;
  const { showStartEllipsis, showEndEllipsis, group, pageIndex } =
    usePagination({
      page,
      totalNoPages,
      perPage,
    });

  return (
    <>
      {showStartEllipsis ? (
        <>
          <PaginationControl page={1} onClick={() => onPageChange?.(1)}>
            1
          </PaginationControl>
          <Ellipsis />
        </>
      ) : null}
      {group?.length
        ? group.map((p) => {
            const active = p === pageIndex;
            return (
              <PaginationControl
                key={p + 1}
                page={p + 1}
                active={active}
                disabled={totalNoPages === 1}
                onClick={() => {
                  onPageChange?.(p + 1);
                }}
              >
                <span className="sr-only">
                  {active ? texts.selectedPageAriaLabel : texts.pageAriaLabel}
                </span>
                {p + 1}
              </PaginationControl>
            );
          })
        : null}
      {showEndEllipsis ? (
        <>
          <Ellipsis />
          <PaginationControl
            page={totalNoPages}
            onClick={() => onPageChange?.(totalNoPages)}
          >
            {totalNoPages}
          </PaginationControl>
        </>
      ) : null}
    </>
  );
};

const iconMap: Record<TDir, { prev: IconDefinition; next: IconDefinition }> = {
  rtl: {
    prev: faChevronRight,
    next: faChevronLeft,
  },
  ltr: {
    prev: faChevronLeft,
    next: faChevronRight,
  },
};

export const Pagination: FC<PaginationProps> = ({
  dir,
  type,
  className,
  page,
  totalNoItems,
  onPageChange,
  onPreviousPage,
  onNextPage,
  setPageParams,
  pageSizeOptions,
  texts,
}) => {
  const [pageSize, setPageSize] = useState<PaginationPageSize>(
    pageSizeOptions?.pageSize || 5,
  );

  const PaginationControl =
    type === 'button' ? PaginationButton : PaginationLink;

  const totalNoPages =
    typeof pageSize === 'number' ? Math.ceil(totalNoItems / pageSize) : 1;

  useEffect(() => {
    if (totalNoPages && page > totalNoPages) {
      setPageParams?.(totalNoPages.toString());
    }
    if (totalNoPages && page < 1) {
      setPageParams?.('1');
    }
  }, [page, setPageParams, totalNoPages]);

  const icons = iconMap[dir || 'ltr'];

  const previous = (
    <PaginationControl
      page={page - 1}
      className="me-auto"
      disabled={page === 1}
      onClick={() => {
        onPageChange?.(page - 1);
        onPreviousPage?.(page - 1);
      }}
    >
      <FaIcon icon={icons.prev} height={16} width={16} />
      <span className="hidden md:inline-block">{texts.prev}</span>
    </PaginationControl>
  );

  const next = (
    <PaginationControl
      page={page + 1}
      disabled={page === totalNoPages}
      className="ms-auto"
      onClick={() => {
        onPageChange?.(page + 1);
        onNextPage?.(page + 1);
      }}
    >
      <span className="hidden md:inline-block">{texts.next}</span>
      <FaIcon icon={icons.next} height={16} width={16} />
    </PaginationControl>
  );

  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-between gap-4',
        className,
      )}
    >
      <nav className="flex items-center" aria-label={texts.ariaLabel}>
        <h2 className="sr-only">{texts.ariaTitle}</h2>
        <ol data-component="pagination" className="flex items-center gap-x-6 ">
          {previous}
          <div className="flex gap-x-1 md:gap-x-2">
            <Pages
              type={type}
              page={page}
              totalNoPages={totalNoPages}
              perPage={typeof pageSize === 'number' ? pageSize : totalNoItems}
              onPageChange={onPageChange}
              texts={texts}
            />
          </div>

          {next}
        </ol>
      </nav>
      <PageSizeSelect
        label={pageSizeOptions?.label}
        selectedKey={pageSize}
        onSelect={(item) => {
          setPageSize(item);
          pageSizeOptions?.onPageSizeChange?.(item);
        }}
        container={pageSizeOptions?.container as HTMLElement}
        items={pageSizeOptions?.options}
      />
    </div>
  );
};
